import {Badge, Box, Stack, Text, Tooltip} from '@mantine/core'
import {useEffect, useId, useMemo, useRef, useState} from 'react'

import type {GiftCommitmentsData} from 'src/api/client/models/analytics'
import {formatCurrency, formatNumber} from 'src/lib/format'

export type DonorPyramidRow = GiftCommitmentsData['rows'][number]

/** Full artboard from `pyramid-levels.svg` (includes pointed base / 3D kite). */
const VIEW_WIDTH = 1100
const VIEW_HEIGHT = 932

/**
 * Nine faceted left/right polygon pairs from `pyramid-levels.svg`, ordered top → bottom
 * (apex to base), paired by centroid Y. This reproduces the “folded” 3D look — not flat
 * horizontal cuts.
 */
const PYRAMID_FACETS: readonly {left: string; right: string}[] = [
    {
        left: '490.34 87.34 546.54 99.85 546.54 4.68 490.34 87.34',
        right: '552.9 4.68 552.9 99.85 609.1 87.34 552.9 4.68',
    },
    {
        left: '546.54 105.72 486.87 92.43 429.23 177.22 546.54 203.34 546.54 105.72',
        right: '552.9 203.34 670.21 177.22 612.56 92.43 552.9 105.72 552.9 203.34',
    },
    {
        left: '546.54 306.84 546.54 209.21 425.76 182.32 368.11 267.11 546.54 306.84',
        right: '552.9 306.84 731.32 267.11 673.68 182.32 552.9 209.21 552.9 306.84',
    },
    {
        left: '546.54 410.33 546.54 312.7 364.65 272.2 307 357 546.54 410.33',
        right: '552.9 410.33 792.44 357 734.79 272.21 552.9 312.7 552.9 410.33',
    },
    {
        left: '546.54 513.82 546.54 416.2 303.54 362.09 245.89 446.88 546.54 513.82',
        right: '552.9 513.82 853.55 446.88 795.9 362.09 552.9 416.2 552.9 513.82',
    },
    {
        left: '546.54 617.32 546.54 519.69 242.42 451.98 184.77 536.77 546.54 617.32',
        right: '552.9 617.32 914.66 536.77 857.01 451.98 552.9 519.69 552.9 617.32',
    },
    {
        left: '546.54 720.81 546.54 623.18 181.31 541.86 123.66 626.65 546.54 720.81',
        right: '552.9 720.81 975.77 626.65 918.13 541.86 552.9 623.18 552.9 720.81',
    },
    {
        left: '546.54 726.68 120.2 631.75 62.55 716.54 546.54 824.3 546.54 726.68',
        right: '552.9 824.3 1036.89 716.54 979.24 631.75 552.9 726.68 552.9 824.3',
    },
    {
        left: '546.54 830.17 59.09 721.63 0 808.54 546.54 930.73 546.54 830.17',
        right: '552.9 830.17 552.9 930.73 1099.44 808.54 1040.35 721.63 552.9 830.17',
    },
]

const VISUAL_BANDS = PYRAMID_FACETS.length

const OUTLINE_POINTS = '549.72 0 0 808.54 549.72 931.44 1099.44 808.54 549.72 0'

const PYRAMID_CENTER_X = 549.72

const TIER_COLORS = [
    {left: '#a9ce91', right: '#70ad47'},
    {left: '#cfc792', right: '#afa249'},
    {left: '#f5c093', right: '#ee964b'},
] as const

/** Vertical anchor for each tier title (from `pyramid-levels.svg`); rect is derived with padding. */
const TIER_LABEL_SPECS = [
    {tier: 0 as const, label: 'MAJOR' as const, centerY: 162.36},
    {tier: 1 as const, label: 'MID-LEVEL' as const, centerY: 466.25},
    {tier: 2 as const, label: 'GRASSROOTS' as const, centerY: 774.03},
] as const

const LABEL_FONT_SIZE = 51.75
/** Horizontal / vertical padding inside the label chip (SVG user units). */
const LABEL_PAD_X = 16
const LABEL_PAD_Y = 12

/** Blend hex color toward white (0 = unchanged, 1 = white). */
function mixHexWithWhite(hex: string, t: number): string {
    const h = hex.startsWith('#') ? hex.slice(1) : hex
    const r = parseInt(h.slice(0, 2), 16)
    const g = parseInt(h.slice(2, 4), 16)
    const b = parseInt(h.slice(4, 6), 16)
    const mix = (c: number) => Math.round(c + (255 - c) * t)
    const toHex = (n: number) => n.toString(16).padStart(2, '0')
    return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`
}

const HOVER_LIGHTEN = 0.22

type Props = {
    rows: DonorPyramidRow[]
}

function giftLevelLabel(giftSize: number): string {
    return giftSize >= 10_000 ? formatCurrency(giftSize) : 'Under $10,000'
}

function isRequirementMet(numberRequired: number | null, numberIdentified: number): boolean {
    return numberRequired === null || numberIdentified >= numberRequired
}

/** Three MAJOR facets, three MID-LEVEL, three GRASSROOTS — matches source art. */
function tierIndexForBand(bandIndex: number): 0 | 1 | 2 {
    return Math.min(2, Math.floor(bandIndex / 3)) as 0 | 1 | 2
}

/** Centered chip on the pyramid spine; text uses dominantBaseline middle so the box is symmetric. */
function paddedLabelLayout(
    label: string,
    centerY: number,
): {
    rectX: number
    rectY: number
    width: number
    height: number
    textX: number
    textY: number
} {
    const textWidth = label.length * LABEL_FONT_SIZE * 0.55
    const width = textWidth + 2 * LABEL_PAD_X
    const height = LABEL_FONT_SIZE + 2 * LABEL_PAD_Y
    return {
        rectX: PYRAMID_CENTER_X - width / 2,
        rectY: centerY - height / 2,
        width,
        height,
        textX: PYRAMID_CENTER_X,
        textY: centerY,
    }
}

function parsePoints(s: string): [number, number][] {
    const nums = s.trim().split(/\s+/).map(Number)
    const pts: [number, number][] = []
    for (let i = 0; i + 1 < nums.length; i += 2) {
        const x = nums[i]
        const y = nums[i + 1]
        if (x !== undefined && y !== undefined) {
            pts.push([x, y])
        }
    }
    return pts
}

function bboxFromFacet(
    left: string,
    right: string,
): {
    x: number
    y: number
    width: number
    height: number
} {
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const [x, y] of [...parsePoints(left), ...parsePoints(right)]) {
        minX = Math.min(minX, x)
        minY = Math.min(minY, y)
        maxX = Math.max(maxX, x)
        maxY = Math.max(maxY, y)
    }
    return {x: minX, y: minY, width: maxX - minX, height: maxY - minY}
}

function dataRowIndexForBand(bandIndex: number, dataCount: number): number {
    if (dataCount <= 1) return 0
    return Math.min(dataCount - 1, Math.round((bandIndex * (dataCount - 1)) / (VISUAL_BANDS - 1)))
}

function rowForBand(sortedRows: DonorPyramidRow[], bandIndex: number): DonorPyramidRow {
    const idx = dataRowIndexForBand(bandIndex, sortedRows.length)
    const row = sortedRows[idx]
    if (!row) {
        throw new Error('DonorPyramid: missing campaign gap row')
    }
    return row
}

export default function DonorPyramid({rows}: Props) {
    const rawId = useId()
    const hatchId = `gap-hatch-${rawId.replace(/:/g, '')}`
    const [hoveredSortOrder, setHoveredSortOrder] = useState<number | null>(null)
    const hoverClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const sortedRows = useMemo(() => [...rows].sort((a, b) => a.sortOrder - b.sortOrder), [rows])

    const n = sortedRows.length

    const clearHoverLeaveTimer = () => {
        if (hoverClearTimerRef.current) {
            clearTimeout(hoverClearTimerRef.current)
            hoverClearTimerRef.current = null
        }
    }

    const onFacetPointerEnter = (sortOrder: number) => {
        clearHoverLeaveTimer()
        setHoveredSortOrder(sortOrder)
    }

    const onFacetPointerLeave = () => {
        clearHoverLeaveTimer()
        hoverClearTimerRef.current = setTimeout(() => {
            setHoveredSortOrder(null)
            hoverClearTimerRef.current = null
        }, 45)
    }

    useEffect(() => () => clearHoverLeaveTimer(), [])

    if (n === 0) {
        return (
            <Box py="xl">
                <Text c="dimmed" size="sm" ta="center">
                    No gift table data to display.
                </Text>
            </Box>
        )
    }

    return (
        <Box w="100%" maw={720} mx="auto">
            <svg
                viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
                width="100%"
                height="auto"
                role="img"
                aria-label="Donor pyramid by gift level"
            >
                <defs>
                    <pattern
                        id={hatchId}
                        patternUnits="userSpaceOnUse"
                        width={20}
                        height={8}
                        patternTransform="rotate(45)"
                    >
                        <line
                            x1={0}
                            y1={0}
                            x2={0}
                            y2={8}
                            stroke="var(--mantine-color-purple-9)"
                            strokeOpacity={0.3}
                            strokeWidth={8}
                            strokeLinecap="square"
                        />
                    </pattern>
                </defs>

                <polygon fill="#fff" points={OUTLINE_POINTS} />

                {PYRAMID_FACETS.map((facet, bandIndex) => {
                    const row = rowForBand(sortedRows, bandIndex)
                    const tier = tierIndexForBand(bandIndex)
                    const {left: leftBase, right: rightBase} = TIER_COLORS[tier]
                    const isRowHovered =
                        hoveredSortOrder !== null && row.sortOrder === hoveredSortOrder
                    const leftFill = isRowHovered
                        ? mixHexWithWhite(leftBase, HOVER_LIGHTEN)
                        : leftBase
                    const rightFill = isRowHovered
                        ? mixHexWithWhite(rightBase, HOVER_LIGHTEN)
                        : rightBase
                    const met = isRequirementMet(row.numberRequired, row.numberIdentified)
                    const isGap = !met

                    return (
                        <g key={`band-${bandIndex}`} data-testid="donor-pyramid-slice">
                            <polygon
                                points={facet.left}
                                fill={leftFill}
                                stroke="#fff"
                                strokeWidth={1}
                            />
                            <polygon
                                points={facet.right}
                                fill={rightFill}
                                stroke="#fff"
                                strokeWidth={1}
                            />
                            {isGap ? (
                                <>
                                    <polygon
                                        data-testid="gap-hatch-overlay"
                                        points={facet.left}
                                        fill={`url(#${hatchId})`}
                                        pointerEvents="none"
                                    />
                                    <polygon
                                        data-testid="gap-hatch-overlay"
                                        points={facet.right}
                                        fill={`url(#${hatchId})`}
                                        pointerEvents="none"
                                    />
                                </>
                            ) : null}
                            {isRowHovered ? (
                                <>
                                    <polygon
                                        points={facet.left}
                                        fill="#fff"
                                        fillOpacity={0.12}
                                        pointerEvents="none"
                                    />
                                    <polygon
                                        points={facet.right}
                                        fill="#fff"
                                        fillOpacity={0.12}
                                        pointerEvents="none"
                                    />
                                </>
                            ) : null}
                        </g>
                    )
                })}

                {TIER_LABEL_SPECS.map(({tier, label, centerY}) => {
                    const bg = TIER_COLORS[tier].right
                    const {rectX, rectY, width, height, textX, textY} = paddedLabelLayout(
                        label,
                        centerY,
                    )
                    return (
                        <g key={label} pointerEvents="none">
                            <rect
                                x={rectX}
                                y={rectY}
                                width={width}
                                height={height}
                                fill={bg}
                                rx={4}
                            />
                            <text
                                x={textX}
                                y={textY}
                                fill="#fff"
                                fontFamily="Source Sans Pro, system-ui, sans-serif"
                                fontSize={LABEL_FONT_SIZE}
                                fontWeight={600}
                                textAnchor="middle"
                                dominantBaseline="middle"
                            >
                                {label}
                            </text>
                        </g>
                    )
                })}

                {PYRAMID_FACETS.map((facet, bandIndex) => {
                    const row = rowForBand(sortedRows, bandIndex)
                    const bbox = bboxFromFacet(facet.left, facet.right)
                    const met = isRequirementMet(row.numberRequired, row.numberIdentified)
                    const giftLabel = giftLevelLabel(row.giftSize)
                    const requiredLabel =
                        row.numberRequired != null ? formatNumber(row.numberRequired) : 'Many'
                    const identifiedLabel = formatNumber(row.numberIdentified)

                    const tooltip = (
                        <Stack gap={6} p={4}>
                            <Text fw={700} size="sm">
                                {giftLabel}
                            </Text>
                            <Text size="sm">
                                Required:{' '}
                                <Text span fw={600}>
                                    {requiredLabel}{' '}
                                </Text>
                                {met ? (
                                    <Badge color="green" variant="light" size="sm">
                                        MET
                                    </Badge>
                                ) : (
                                    <Badge color="grape" variant="light" size="sm">
                                        GAP
                                    </Badge>
                                )}
                            </Text>
                            <Text size="sm">
                                Identified:{' '}
                                <Text span fw={600}>
                                    {identifiedLabel}{' '}
                                </Text>
                                {row.numberRequired != null
                                    ? met
                                        ? '✓'
                                        : `(-${formatNumber(row.numberRequired - row.numberIdentified)})`
                                    : '—'}
                            </Text>
                        </Stack>
                    )

                    return (
                        <Tooltip
                            key={`hit-${bandIndex}`}
                            label={tooltip}
                            position="top"
                            withArrow
                            openDelay={200}
                            styles={{
                                tooltip: {
                                    backgroundColor: 'var(--mantine-color-white)',
                                    color: 'var(--mantine-color-gray-9)',
                                    border: '1px solid var(--mantine-color-gray-3)',
                                    boxShadow: 'var(--mantine-shadow-md)',
                                },
                                arrow: {
                                    backgroundColor: 'var(--mantine-color-white)',
                                },
                            }}
                        >
                            <rect
                                data-testid="donor-pyramid-hit"
                                x={bbox.x}
                                y={bbox.y}
                                width={bbox.width}
                                height={bbox.height}
                                fill="transparent"
                                onPointerEnter={() => onFacetPointerEnter(row.sortOrder)}
                                onPointerLeave={onFacetPointerLeave}
                            />
                        </Tooltip>
                    )
                })}
            </svg>
        </Box>
    )
}
